"use client";

// Step 8.5 Half A0 commit 0.2 / AC406, AC408: wizard Step 2 - business
// profile. Business-type dropdown (incl. Personal event, which
// branches Step 3), years-in-business + monthly-budget radio grids,
// plain-text business address (see Address note below), optional
// website, how-heard dropdown.
//
// Address note (2026-06-08 hotfix r3): use-places-autocomplete was
// dropped from the wizard. The library relies on
// google.maps.places.AutocompleteService, which Google deprecated for
// projects created after 2025-03-01. Magnaflor's GCP project is
// post-cutoff, so the legacy service is unavailable - it can never
// work for us. We persist addressVerified=false on every signup; admin
// verifies manually at approval time. Step 12 will migrate to
// PlaceAutocompleteElement (Places API New) when the rest of the
// portal stabilises.

import { useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import type { SignupFormValues } from "./SignupWizard";
import { SignupFulfillment } from "./SignupFulfillment";
import {
  fieldClass,
  labelClass,
  primaryButtonClass,
  radioCardClass,
  radioCardSelectedClass,
  secondaryButtonClass,
} from "./styles";
import { DeliveryFeePreview } from "@/components/delivery/DeliveryFeePreview";
import { US_STATES } from "@/lib/signup/us-states";
import { isOutOfStateByZip } from "@/lib/delivery/zone-lookup";

const YEARS_OPTIONS = ["less-than-1", "1-3", "3-10", "10-plus"] as const;
const YEARS_LABELS: Record<(typeof YEARS_OPTIONS)[number], string> = {
  "less-than-1": "Less than 1",
  "1-3": "1 - 3 years",
  "3-10": "3 - 10 years",
  "10-plus": "10+ years",
};

const BUDGET_OPTIONS = ["under-500", "500-2000", "2000-10000", "10000-plus"] as const;
const BUDGET_LABELS: Record<(typeof BUDGET_OPTIONS)[number], string> = {
  "under-500": "Under $500",
  "500-2000": "$500 - $2,000",
  "2000-10000": "$2,000 - $10,000",
  "10000-plus": "$10,000+",
};

const BUSINESS_TYPES = [
  { value: "florist", label: "Florist" },
  { value: "supermarket", label: "Supermarket" },
  { value: "event-planner", label: "Event planner" },
  { value: "wedding-planner", label: "Wedding planner" },
  { value: "hotel", label: "Hotel / Resort" },
  { value: "wholesaler", label: "Wholesaler / Reseller" },
  { value: "personal-event", label: "Personal event (wedding, party, etc.)" },
  { value: "other", label: "Other" },
];

const HOW_HEARD = [
  "Google search",
  "Referral from another florist",
  "Instagram",
  "Trade show / industry event",
  "Other",
];

// #346: the fee/zone preview now reads the dedicated ZIP field (not a ZIP
// scraped from free text). Its FL-fee vs out-of-state-pickup rendering is
// UNCHANGED - only the input is more reliable. It no longer DRIVES isOutOfState
// (state does); the preview is purely informational.
function ZipDeliveryPreview() {
  const zip = useWatch<SignupFormValues, "zip">({ name: "zip", defaultValue: "" });
  return (
    <DeliveryFeePreview
      zip={(zip ?? "").trim()}
      testIdPrefix="signup-delivery-preview"
    />
  );
}

function AddressAutocomplete({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (v: string) => void;
  error?: string;
}) {
  // #350: plain full-width street input - no pin/emoji icon.
  return (
    <>
      <input
        type="text"
        data-testid="signup-businessAddress"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="1240 NW 22nd Ave"
        className={`mt-1 ${fieldClass}`}
      />
      <p className="mt-1 text-xs font-normal text-zinc-500">
        Enter your street address - we&apos;ll verify on our end.
      </p>
      {error ? (
        <p className="mt-1 text-xs font-normal text-red-600">{error}</p>
      ) : null}
    </>
  );
}

export function SignupStep2({
  submitError,
  onBack,
  onContinue,
}: {
  submitError?: string | null;
  onBack: () => void;
  onContinue: () => void;
}) {
  const {
    register,
    control,
    formState: { errors },
    trigger,
    watch,
  } = useFormContext<SignupFormValues>();

  const [shipError, setShipError] = useState<string | null>(null);
  const zip = watch("zip");
  const signupLocale = watch("preferredLanguage") === "es" ? "es" : "en";
  // #442: out-of-state is driven off the ZIP - the SAME check the delivery-fee
  // banner + the fee resolver use - so the "how should we get your flowers?"
  // question shows/hides in lockstep with the fee shown + charged. (Was #346
  // STATE-based, which contradicted the ZIP-based banner: State=CA + a FL ZIP
  // showed BOTH a FL zone fee AND the out-of-state options.) The ZIP is a
  // dedicated, validated field now, so keying off it is reliable.
  const isOutOfState = isOutOfStateByZip(zip);

  async function handleContinue() {
    const fields: (keyof SignupFormValues | `fulfillment.${string}`)[] = [
      "businessType",
      "yearsInBusiness",
      "businessAddress",
      "city",
      "state",
      "zip",
      "monthlyBudget",
    ];
    const ok = await trigger(
      fields as Parameters<typeof trigger>[0],
      { shouldFocus: true },
    );
    if (!ok) return;

    // #432: out-of-state shipping gate. AIRPORT requires a CONFIRMED airport;
    // OWN_CARRIER requires the #235 hand-off details. DOOR needs nothing extra.
    if (isOutOfState) {
      const method = watch("shippingMethod") || "AIRPORT";
      if (method === "AIRPORT" && !watch("shippingAirportConfirmed")) {
        setShipError("Confirm your pickup airport to continue.");
        return;
      }
      if (method === "OWN_CARRIER") {
        const choice = watch("fulfillment.choice");
        if (!choice) {
          setShipError("Choose how we should hand off your boxes in Miami.");
          return;
        }
        if (
          choice === "trucker" &&
          (!watch("fulfillment.truckerName")?.trim() ||
            !watch("fulfillment.truckerContact")?.trim())
        ) {
          setShipError("Enter your trucking company name + contact.");
          return;
        }
      }
    }
    setShipError(null);
    onContinue();
  }

  return (
    <div data-testid="signup-step-2">
      <h1 className="mb-2 text-center text-2xl font-semibold text-zinc-900">
        Tell us about your business
      </h1>
      <p className="mb-8 text-center text-sm text-zinc-500">
        This helps us match you to the right pricing and approve faster.
      </p>

      {submitError ? (
        <p
          data-testid="signup-step-error"
          className="mb-6 rounded-md bg-red-50 p-3 text-sm text-red-700"
        >
          {submitError}
        </p>
      ) : null}

      <label className={labelClass}>
        Business type <span className="text-red-600">*</span>
        {/* URGENT signup fix: Controller so the value survives the step
            unmount/remount (register'd inputs validated as EMPTY at the final
            submit -> false "required"). */}
        <Controller
          control={control}
          name="businessType"
          rules={{ required: "Pick a business type." }}
          render={({ field }) => (
            <select
              data-testid="signup-businessType"
              value={field.value ?? ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              className={fieldClass}
            >
              <option value="">— Select one —</option>
              {BUSINESS_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          )}
        />
        {errors.businessType ? (
          <span className="mt-1 text-xs text-red-600">
            {errors.businessType.message as string}
          </span>
        ) : null}
      </label>

      <label className={labelClass}>
        Years in business <span className="text-red-600">*</span>
        {/* URGENT signup fix: Controller (not a hidden input + setValue) so the
            value can never desync across the step unmount and silently fail the
            final submit's validation. */}
        <Controller
          control={control}
          name="yearsInBusiness"
          rules={{ required: "Pick how long you've been in business." }}
          render={({ field }) => (
            <div className="mt-1 grid grid-cols-2 gap-2">
              {YEARS_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  data-testid={`signup-years-${opt}`}
                  onClick={() => field.onChange(opt)}
                  className={
                    field.value === opt ? radioCardSelectedClass : radioCardClass
                  }
                >
                  {YEARS_LABELS[opt]}
                </button>
              ))}
            </div>
          )}
        />
        {errors.yearsInBusiness ? (
          <span className="mt-1 text-xs text-red-600">
            {errors.yearsInBusiness.message as string}
          </span>
        ) : null}
      </label>

      {/* #350: street address - full-width, no pin icon, helper below. */}
      <label className={labelClass}>
        Street address <span className="text-red-600">*</span>
        <Controller
          control={control}
          name="businessAddress"
          rules={{ required: "Street address is required." }}
          render={({ field }) => (
            <AddressAutocomplete
              value={field.value}
              onChange={field.onChange}
              error={errors.businessAddress?.message as string | undefined}
            />
          )}
        />
      </label>

      {/* #346 fields, #350 layout: City / State / ZIP in one aligned row on
          desktop (City widest, State + ZIP narrower), stacked on mobile. Each
          cell is a flex column (label above field) with min-w-0 so the full-
          width inputs never collapse. Proportional columns (no `auto`, which
          starves w-full inputs). */}
      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
        <label className="flex min-w-0 flex-col gap-1 text-sm font-semibold text-zinc-900">
          <span>
            City <span className="text-red-600">*</span>
          </span>
          <Controller
            control={control}
            name="city"
            rules={{ required: "City is required." }}
            render={({ field }) => (
              <input
                type="text"
                data-testid="signup-city"
                value={field.value ?? ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                className={fieldClass}
              />
            )}
          />
          {errors.city ? (
            <span className="text-xs font-normal text-red-600">
              {errors.city.message as string}
            </span>
          ) : null}
        </label>
        <label className="flex min-w-0 flex-col gap-1 text-sm font-semibold text-zinc-900">
          <span>
            State <span className="text-red-600">*</span>
          </span>
          <Controller
            control={control}
            name="state"
            rules={{ required: "Select your state." }}
            render={({ field }) => (
              <select
                data-testid="signup-state"
                value={field.value ?? ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                className={fieldClass}
              >
                <option value="">—</option>
                {US_STATES.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.code}
                  </option>
                ))}
              </select>
            )}
          />
          {errors.state ? (
            <span className="text-xs font-normal text-red-600">
              {errors.state.message as string}
            </span>
          ) : null}
        </label>
        <label className="flex min-w-0 flex-col gap-1 text-sm font-semibold text-zinc-900">
          <span>
            ZIP <span className="text-red-600">*</span>
          </span>
          <Controller
            control={control}
            name="zip"
            rules={{
              required: "ZIP is required.",
              pattern: { value: /^\d{5}$/, message: "Enter a 5-digit ZIP." },
            }}
            render={({ field }) => (
              <input
                type="text"
                inputMode="numeric"
                maxLength={5}
                data-testid="signup-zip"
                value={field.value ?? ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                className={fieldClass}
              />
            )}
          />
          {errors.zip ? (
            <span className="text-xs font-normal text-red-600">
              {errors.zip.message as string}
            </span>
          ) : null}
        </label>
      </div>

      {/* DIRECTIVE-040 commit 5 / #444: FL delivery-fee preview. Shown ONLY for
          Florida addresses (zone fee / Doral pickup). For an OUT-OF-STATE address
          the legacy "Free pickup in Doral" fallback is misleading (they won't
          drive to Miami; "Free" would sit above the paid door/airport options),
          so we HIDE it and let the out-of-state selector below be the single
          source of truth. isOutOfState is the same ZIP predicate the selector +
          fee resolver use (#442). */}
      {!isOutOfState && <ZipDeliveryPreview />}

      {/* PASTE #235 / #442: out-of-state fulfillment capture - gated on the ZIP
          (isOutOfStateByZip), in lockstep with the delivery-fee banner above. */}
      <SignupFulfillment isOutOfState={isOutOfState} locale={signupLocale} />
      {shipError ? (
        <p
          data-testid="signup-shipping-error"
          className="mt-2 text-sm text-red-600"
        >
          {shipError}
        </p>
      ) : null}

      <label className={labelClass}>
        Business website <span className="font-normal text-zinc-500">(optional)</span>
        <input
          type="url"
          placeholder="https://lilysbloomboutique.com"
          data-testid="signup-website"
          {...register("website")}
          className={fieldClass}
        />
      </label>

      <label className={labelClass}>
        Estimated monthly flower budget <span className="text-red-600">*</span>
        <Controller
          control={control}
          name="monthlyBudget"
          rules={{ required: "Pick your estimated monthly flower budget." }}
          render={({ field }) => (
            <div className="mt-1 grid grid-cols-2 gap-2">
              {BUDGET_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  data-testid={`signup-budget-${opt}`}
                  onClick={() => field.onChange(opt)}
                  className={
                    field.value === opt ? radioCardSelectedClass : radioCardClass
                  }
                >
                  {BUDGET_LABELS[opt]}
                </button>
              ))}
            </div>
          )}
        />
        {errors.monthlyBudget ? (
          <span className="mt-1 text-xs text-red-600">
            {errors.monthlyBudget.message as string}
          </span>
        ) : null}
      </label>

      <label className={labelClass}>
        How did you hear about Magnaflor?{" "}
        <span className="font-normal text-zinc-500">(optional)</span>
        <select
          data-testid="signup-howHeard"
          {...register("howHeard")}
          className={fieldClass}
        >
          <option value="">— Select one —</option>
          {HOW_HEARD.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      </label>

      <div className="mt-2 flex gap-3">
        <button
          type="button"
          data-testid="signup-step-2-back"
          onClick={onBack}
          className={secondaryButtonClass}
        >
          ← Back
        </button>
        <button
          type="button"
          data-testid="signup-step-2-continue"
          onClick={handleContinue}
          className={`${primaryButtonClass} flex-1`}
        >
          Continue →
        </button>
      </div>
    </div>
  );
}
